/**
 * /security — Public-facing security posture (marketing surface).
 *
 * Distinct from /legal/security (legal policy text) and /app/security (in-app
 * settings). Renders a SOC2-readiness-style posture statement for prospects.
 *
 * Replaces the Phase 9 ComingSoonShell stub per Phase 3 Task 3.7 of the
 * 2026-05-16 implementation plan.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { MarketingLayout } from '../components/layouts/marketing-layout';

export const Route = createFileRoute('/security')({
  component: SecurityPage,
});

function SecurityPage() {
  useEffect(() => {
    document.title = 'Security · Elreign: POPIA-aligned, audit-chained, in South Africa';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'Elreign security posture: data residency in southafricanorth, AES-256 encryption at rest (field-level Always Encrypted for PII at GA), JWKS-based session revocation, WORM audit chain, multi-tenant isolation, MFA, and 72-hour breach notification.',
    );
  }, []);

  return (
    <MarketingLayout>
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '72px 24px 96px' }}>
        {/* Hero */}
        <header style={{ marginBottom: 56 }}>
          <p className="eyebrow" style={{ marginBottom: 18 }}>Security posture</p>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(40px, 6vw, 64px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
            }}
          >
            Trust records are <em>irreplaceable.</em>
          </h1>
          <p
            style={{
              fontSize: 19,
              color: 'var(--ink-3)',
              lineHeight: 1.65,
              maxWidth: 640,
              margin: 0,
            }}
          >
            We start our security design from that premise, not from minimum compliance. The
            architecture described here is the basis of our SOC 2 readiness programme; controls
            noted as activating at general availability are built and staged for that milestone.
          </p>
        </header>

        {/* At a glance grid */}
        <section
          aria-label="Security at a glance"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16,
            marginBottom: 64,
          }}
        >
          {[
            { eyebrow: 'Residency',   value: 'southafricanorth',     sub: 'Cape Town + Johannesburg' },
            { eyebrow: 'Encryption',  value: 'AES-256 at rest',      sub: 'Field-level AE at GA' },
            { eyebrow: 'Audit',       value: 'WORM · 5y',            sub: 'POPIA s14 + FICA s23' },
            { eyebrow: 'Breach',      value: '72-hour',              sub: 'POPIA s22 notification' },
          ].map(({ eyebrow, value, sub }) => (
            <div
              key={eyebrow}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--hairline)',
                borderRadius: 'var(--r-md)',
                padding: '20px 22px',
              }}
            >
              <p className="eyebrow" style={{ marginBottom: 8 }}>{eyebrow}</p>
              <div
                className="serif"
                style={{
                  fontSize: 22,
                  fontStyle: 'italic',
                  fontWeight: 300,
                  color: 'var(--ink)',
                  letterSpacing: '-0.01em',
                  marginBottom: 4,
                  lineHeight: 1.2,
                }}
              >
                {value}
              </div>
              <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: 0 }}>{sub}</p>
            </div>
          ))}
        </section>

        {/* Sections */}
        <Section eyebrow="POPIA s19" heading="Compliance by design, not as afterthought">
          <p>
            We are an Operator under the Protection of Personal Information Act. Every architectural
            decision has been tested against section 19 (general security obligations), section 20
            (the operator&apos;s independent duty to safeguard), and the conditions in chapter 3.
            Personal information collected and processed for trust administration purposes never
            leaves South Africa. Authentication metadata, and only authentication metadata, is
            stored in Supabase EU West, which is permitted under POPIA s72 by the European Union
            adequacy decision.
          </p>
        </Section>

        <Section eyebrow="Cosmos DB" heading="Field-level encryption for personal information">
          <p>
            Trust records, beneficial-ownership entries, FICA documents, and signing artefacts are
            stored in Azure Cosmos DB in South Africa North, encrypted at rest by default. On top of
            that at-rest layer, our architecture adds field-level encryption (Cosmos DB&apos;s Always
            Encrypted feature, with deterministic and randomised key types as appropriate) for the
            most sensitive personal-information fields: identification numbers, contact details, and
            financial particulars. This client-side field-level layer activates at general
            availability, once the customer-managed key hierarchy is provisioned; when live, the
            database engine sees only ciphertext for those fields.
          </p>
        </Section>

        <Section eyebrow="Identity" heading="JWT + JWKS revocation">
          <p>
            User sessions are issued as JSON Web Tokens, signed by a key advertised through a JWKS
            endpoint that the API verifies on every request. When a session must be revoked (a
            laptop is reported lost, a trustee resigns, an MLRO changes role), the affected key
            material is rotated and the previous token is rejected on the next call. We do not rely
            on cookie expiry for revocation; the validity decision is made server-side, every
            request.
          </p>
        </Section>

        <Section eyebrow="Audit" heading="WORM-locked. Five-year minimum.">
          <p>
            Every event is written to an immutable Azure Blob Storage container under a write-once,
            read-many policy enforced at the platform level. Existing records cannot be modified or
            deleted, by Elreign staff or by anyone else. The default retention window is five years
            from the date of last activity per FICA s23; longer windows are available on request and
            are commonly used by tenants serving long-life estates and inter-generational trusts.
          </p>
          <p>
            Records carry a SHA-256 content hash and a chain hash linking to the preceding record.
            Any tampering with a single record breaks the chain and is detected on verification,
            independently of Elreign.
          </p>
        </Section>

        <Section eyebrow="Tenancy" heading="Multi-tenant isolation, per L13 and L25">
          <p>
            Every database call carries a tenant identifier derived from the verified JWT subject,
            not from request input. Cosmos DB containers are partitioned by tenant; the
            authorisation layer rejects any cross-tenant read or write before it reaches the storage
            engine. The architectural decision is captured in Decision L13 (tenant isolation
            boundary) and L25 (managed-identity scope grants), both available on request in our
            Trust Centre.
          </p>
        </Section>

        <Section eyebrow="Authentication" heading="MFA required for all platform users">
          <p>
            Multi-factor authentication is mandatory for all administrators and recommended for
            beneficiaries who hold a portal account. TOTP, WebAuthn, and platform biometrics are
            supported. Recovery is by enrolment of a second device or by a witnessed identity check
            with the firm&apos;s MLRO. Step-up authentication is enforced for any signature event,
            any change to the trustee panel, and any change to a payout bank account.
          </p>
        </Section>

        <Section eyebrow="Mobile" heading="Certificate pinning on the iOS and Android app">
          <p>
            The Elreign mobile app pins our TLS certificate fingerprint, preventing on-path
            interception even if the device trust store has been compromised. The signing flow on
            mobile is gated behind a device biometric prompt, and the signing key is held on the
            device in the platform&apos;s secure keystore (iOS Keychain / Android Keystore).
          </p>
        </Section>

        <Section eyebrow="Breach response" heading="72-hour notification under POPIA s22">
          <p>
            In the event of a personal information breach, affected responsible parties (tenant
            firms) are notified within 72 hours of discovery in accordance with POPIA s22. We
            maintain a written incident response plan with severity levels, response SLAs, and
            communication templates currently under external legal review. The Information Regulator is
            notified by Elreign as Operator in coordination with the affected firm as Responsible
            Party.
          </p>
        </Section>

        <Section eyebrow="Disclosure" heading="Vulnerability disclosure">
          <p>
            We welcome responsible disclosure of security vulnerabilities. Reports go to{' '}
            <a href="mailto:security@elreign.io" style={{ color: 'var(--primary-600)' }}>
              security@elreign.io
            </a>
            . We aim to acknowledge within one business day and to provide a resolution timeline
            within five business days. Researchers who follow responsible disclosure are credited
            in our security history with their permission.
          </p>
        </Section>

        {/* Bottom links */}
        <section
          style={{
            marginTop: 64,
            paddingTop: 32,
            borderTop: '1px solid var(--hairline)',
            display: 'flex',
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <Link to="/legal/security" style={linkStyle}>
            Read the full security disclosure
          </Link>
          <Link to="/legal/subprocessors" style={linkStyle}>
            See our sub-processor list
          </Link>
          <Link to="/legal/trust-centre" style={linkStyle}>
            Visit the Trust Centre
          </Link>
        </section>
      </div>
    </MarketingLayout>
  );
}

const linkStyle: React.CSSProperties = {
  fontSize: 15,
  color: 'var(--primary-600)',
  textDecoration: 'none',
  borderBottom: '1px solid rgba(31,77,69,0.2)',
  paddingBottom: 1,
};

interface SectionProps {
  eyebrow: string;
  heading: string;
  children: React.ReactNode;
}

function Section({ eyebrow, heading, children }: SectionProps) {
  return (
    <section
      style={{
        paddingTop: 36,
        marginTop: 36,
        borderTop: '1px solid var(--hairline)',
      }}
    >
      <p className="eyebrow" style={{ marginBottom: 10 }}>{eyebrow}</p>
      <h2
        className="serif"
        style={{
          fontSize: 26,
          fontStyle: 'italic',
          fontWeight: 300,
          color: 'var(--ink)',
          margin: '0 0 16px',
          letterSpacing: '-0.01em',
          lineHeight: 1.25,
        }}
      >
        {heading}
      </h2>
      <div style={{ fontSize: 17, color: 'var(--ink-2)', lineHeight: 1.7 }}>{children}</div>
    </section>
  );
}
